import { Button, Header, LottieIcon, ScrollView } from '$uikit';
import { useDesktop, useTranslator } from '$hooks';
import './NewAdClipPreview.sass';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useRef, useState } from 'react';
import { goBack } from '$navigation/helper';
import { attachActions, attachSelector } from '$store/attach';
import { _confirm, classNames, extractFramesFromVideo, formatFileSize } from '$utils';
import { ProgressCircle } from '$shared/components';
import { IcClose28, IcPlay28 } from '$assets';
import { batchActions } from '$store';
import { imActions } from '$store/im';
import { toastActions } from '$store/toast';
import { showModal } from '../../navigation/helper';
import { AD_TARGETING, NEW_AD_CLIP } from '$shared/constants/modals';
import { adsActions } from '$store/ads';

const FRAMES_COUNT = 10;

enum LoaderContent {
  CLOSE = 'close',
  LOTTIE = 'lottie',
  PLAY = 'play',
}

export const NewAdClipPreview = () => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const isDesktop = useDesktop();
  const [videoSrc, setVideoSrc] = useState('');
  const [frames, setFrames] = useState<string[]>(Array(FRAMES_COUNT).fill(null));
  const [additionalCovers, setAdditionalCovers] = useState<string[]>([]);
  const [isFramesLoading, setIsFramesLoading] = useState(true);
  const [coverImageURL, setCoverImageURL] = useState('');
  const [uploadingProgress, setUploadingProgress] = useState(
    t('upload_video_status_preparing'),
  );
  const [isCovered, setIsCovered] = useState(true);
  const [loaderContent, setLoaderContent] = useState<LoaderContent>(LoaderContent.CLOSE);
  const videoElementRef = useRef<HTMLVideoElement>(null);
  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const {
    video,
    isLoaded,
    loaded,
    total,
    pickerId,
    uploadData,
    isCancelled,
    status,
    error,
  } = useSelector(attachSelector).uploadingVideoData;

  // This screen has no retry UI: a failed upload closes it with a toast, as before.
  useEffect(() => {
    if (status === 'failed') {
      dispatch(toastActions.setToastFail(error?.message || t('video_format_error')));
      dispatch(attachActions.cancelUploadingVideo());
    }
  }, [status]);

  useEffect(() => {
    if (!video) {
      goBack();
    }

    import('../../assets/lottie/loader.json');

    return () => {
      if (!isCancelled) {
        batchActions(
          attachActions.clearUploadingVideoData(),
          attachActions.cancelUploadingVideo(),
          attachActions.setIsAdditionalModalOpened(false),
          imActions.setIsVideoUploading(false),
        );
      }
    };
  }, []);

  useEffect(() => {
    if (isCancelled) {
      goBack();
      batchActions(
        attachActions.clearUploadingVideoData(),
        attachActions.cancelUploadingVideo(),
        attachActions.setIsAdditionalModalOpened(false),
        imActions.setIsVideoUploading(false),
      );
    }
  }, [isCancelled]);

  useEffect(() => {
    if (video) {
      const src = URL.createObjectURL(video);
      setVideoSrc(src);

      extractFramesFromVideo(src, FRAMES_COUNT)
        .then((framesData) => {
          framesData.length && setFrames(Array.from(new Set(framesData)));
          setIsFramesLoading(false);
        })
        .catch((e) => {
          dispatch(toastActions.setToastFail(e.message));
        });
    }
  }, [video]);

  useEffect(() => {
    if (loaded !== total) {
      setUploadingProgress(`${formatFileSize(loaded)} / ${formatFileSize(total)}`);
    }
  }, [loaded, total]);

  useEffect(() => {
    if (!isFramesLoading && !coverImageURL) {
      setCoverImageURL(frames[0]);
    }
  }, [isFramesLoading, coverImageURL]);

  useEffect(() => {
    if (!isLoaded) {
      setLoaderContent(LoaderContent.CLOSE);
    }

    if (isLoaded) {
      setTimeout(() => setLoaderContent(LoaderContent.LOTTIE), 175);
      setUploadingProgress(t('upload_video_status_uploaded'));
    }
  }, [isLoaded]);

  function handleChangeCover(imageURL: string) {
    setIsCovered(true);
    setCoverImageURL(imageURL);
    videoElementRef.current?.pause();
  }

  function handleCancelUploading() {
    _confirm(t('upload_video_cancel_confirmation'), () =>
      dispatch(attachActions.cancelUploadingVideo()),
    );
  }

  function handlePlayVideo() {
    setIsCovered(false);
    videoElementRef.current?.play()?.catch(() => {});
  }

  function handleContinue() {
    dispatch(adsActions.setAdType('reels'));
    dispatch(adsActions.setObjectId(1));
    showModal(AD_TARGETING);
    // uploadData &&
    //   videoElementRef.current &&
    //   dispatch(
    //     attachActions.saveUploadedVideo({
    //       pickerId,
    //       photo: {
    //         isVideo: true,
    //         photoMedium: coverImageURL,
    //         photoLarge: coverImageURL,
    //         width: 500,
    //         height: 400,
    //         photoId: -Date.now(),
    //       },
    //       uploadData: {
    //         ...uploadData,
    //         width: videoElementRef.current.videoWidth,
    //         height: videoElementRef.current.videoHeight,
    //         duration: Math.round(videoElementRef.current.duration),
    //         pickerId,
    //       },
    //     }),
    //   );
    // goBack();
  }

  function renderLoader() {
    if (loaderContent === LoaderContent.LOTTIE) {
      return (
        <LottieIcon
          name="loader"
          size={72}
          onComplete={() => setLoaderContent(LoaderContent.PLAY)}
        />
      );
    }

    return (
      <>
        <ProgressCircle
          progress={(loaded / total) * 100 || 0}
          radius={28}
          strokeWidth={2}
          isSpinning
          isDisableLoader={loaderContent === LoaderContent.PLAY}
        />
        <div className="NewAdClipPreview__loader__icon">
          {loaderContent === LoaderContent.CLOSE ? <IcClose28 /> : <IcPlay28 />}
        </div>
      </>
    );
  }

  if (!video) {
    showModal(NEW_AD_CLIP, true);
    return null;
  }

  return (
    <>
      <Header showBackButton className="NewAdClipPreview__header">
        {t('new_ad_clip_preview')}
      </Header>
      <ScrollView className="NewAdClipPreview">
        <div className="NewAdClipPreview__cont">
          <div
            className="NewAdClipPreview__video"
            ref={videoWrapperRef}
            style={{
              height: videoElementRef.current?.videoHeight,
              maxHeight: isDesktop ? '350px' : videoWrapperRef.current?.clientWidth,
            }}
          >
            <div
              className={classNames('NewAdClipPreview__video__cover', {
                _isEmpty: !coverImageURL,
              })}
            >
              {coverImageURL && (
                <img
                  alt=""
                  className="NewAdClipPreview__loader__cover__image"
                  src={coverImageURL}
                ></img>
              )}
              <div className="NewAdClipPreview__loader__status__label">
                {uploadingProgress}
              </div>
              <div
                className={classNames('NewAdClipPreview__loader', {
                  _isAnimated: loaderContent === LoaderContent.LOTTIE,
                })}
                onClick={isLoaded ? handlePlayVideo : handleCancelUploading}
              >
                {renderLoader()}
              </div>
            </div>
            <video
              controls
              ref={videoElementRef}
              style={{ zIndex: isCovered ? '-1' : '1' }}
            >
              <source type="video/mp4,video/quicktime" src={videoSrc} />
            </video>
          </div>
          <div className="NewAdClipPreview__processing__label">
            {t('upload_video_processing_label')}
          </div>
          {/* <InlineHeader type="sub">{t('upload_video_select_cover')}</InlineHeader> */}
          {/* <Slider className="NewAdClipPreview__slider">
            <div className="NewAdClipPreview__cover__item _uploadBtn">
              <IcUpload28 />
              {t('upload_video_upload_button')}
              <input onChange={handleUploadCover} accept="image/*" type="file" value="" />
            </div>
            {additionalCovers.map((cover) => renderCoverItem(false, cover, cover))}
            {frames.map((frame, index) =>
              renderCoverItem(isFramesLoading, frame, isFramesLoading ? index : frame),
            )}
          </Slider> */}
        </div>
      </ScrollView>
      <Button
        disabled={!isLoaded || !coverImageURL}
        onClick={handleContinue}
        className="NewAdClipPreview__saveButton"
      >
        {t('new_ad_clip_preview_continue')}
      </Button>
    </>
  );
};
